// Posting Monitor — accountant-facing log of everything that auto-posted
// from subsidiary ledgers (M1-M7) into the General Ledger today.
//
// Groups the day's JEs by source type (Invoice, Payment, Bill, Bill Payment,
// Expense, Payroll, Tax Remittance, Reversal, Manual) so an accountant can
// scan what hit the books and click through to any JE's detail page.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Stack, Chip, Button, IconButton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, LinearProgress,
    Alert, ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const GROUP_META = {
    invoice:      { label: 'Invoices Issued',    color: '#0ea5e9', hint: 'DR AR, CR Revenue' },
    payment:      { label: 'Invoice Payments',   color: '#10b981', hint: 'DR Cash, CR AR' },
    bill:         { label: 'Bills Received',     color: '#f59e0b', hint: 'DR Expense, CR AP' },
    bill_payment: { label: 'Bill Payments',      color: '#ef4444', hint: 'DR AP, CR Cash' },
    expense:      { label: 'Expenses',           color: '#8b5cf6', hint: 'DR Expense, CR Cash/CC' },
    payroll_run:  { label: 'Payroll Runs',       color: '#ec4899', hint: 'DR Wages, CR Cash/Liabilities' },
    tax_remit:    { label: 'Tax Remittances',    color: '#14b8a6', hint: 'DR Tax Payable, CR Cash' },
    reverse:      { label: 'Reversals',          color: '#64748b', hint: 'Voids of prior posts' },
    manual:       { label: 'Manual JEs',         color: '#475569', hint: 'Entered by user' },
};

const fmtMoney = (n) => {
    if (n == null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n));
};

export default function PostingMonitor() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(1);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/posting/daily', {
                params: { days },
            });
            setData(res.data?.data || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const totals = data?.totals || {};
    const groups = data?.groups || {};
    const grand = data?.grand_total || {};

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <ReceiptLongIcon sx={{ fontSize: 32, color: '#6366f1' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
                    GL Posting Monitor
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={days}
                    onChange={(_, v) => v && setDays(v)}
                >
                    <ToggleButton value={1}>Today</ToggleButton>
                    <ToggleButton value={7}>7d</ToggleButton>
                    <ToggleButton value={30}>30d</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily }}>
                Every invoice, payment, bill, expense, and payroll run that posts to the general ledger
                shows up here — grouped by source so you can reconcile what hit the books.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Grand total strip */}
            <Paper sx={{ p: 2, mb: 2, borderLeft: '3px solid #6366f1' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Date range</Typography>
                        <Typography variant="body1" sx={{ fontFamily, fontWeight: 600 }}>
                            {data?.from_date} → {data?.to_date}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Entries posted</Typography>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>
                            {grand.count ?? 0}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Total debits</Typography>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#10b981' }}>
                            {fmtMoney(grand.debit)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Total credits</Typography>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#ef4444' }}>
                            {fmtMoney(grand.credit)}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Per-group summary cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {Object.keys(GROUP_META).map((key) => {
                    const meta = GROUP_META[key];
                    const t = totals[key] || { count: 0, debit: 0, credit: 0 };
                    return (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={key}>
                            <Paper sx={{
                                p: 1.5, borderLeft: `3px solid ${meta.color}`,
                                opacity: t.count ? 1 : 0.55, height: '100%',
                            }}>
                                <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: meta.color }}>
                                    {meta.label}
                                </Typography>
                                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>
                                    {t.count}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily, display: 'block' }}>
                                    {fmtMoney(t.debit)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily, fontSize: 10 }}>
                                    {meta.hint}
                                </Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Per-group tables */}
            {Object.keys(GROUP_META).map((key) => {
                const meta = GROUP_META[key];
                const rows = groups[key] || [];
                if (rows.length === 0) return null;
                return (
                    <Paper key={key} sx={{ p: 2, mb: 2, borderLeft: `3px solid ${meta.color}` }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: meta.color, fontFamily }}>
                                {meta.label}
                            </Typography>
                            <Chip size="small" label={`${rows.length} entries`} sx={{ fontFamily }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                {meta.hint}
                            </Typography>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Entry #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Source Ref</TableCell>
                                    <TableCell align="right">Debit</TableCell>
                                    <TableCell align="right">Credit</TableCell>
                                    <TableCell align="center">Open</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((je) => (
                                    <TableRow key={je.id} hover>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily, fontWeight: 600 }}>
                                                {je.entry_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{je.entry_date}</TableCell>
                                        <TableCell>{je.description}</TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" label={je.reference || '—'}
                                                  sx={{ fontFamily, fontSize: 11 }} />
                                        </TableCell>
                                        <TableCell align="right">{fmtMoney(je.total_debit)}</TableCell>
                                        <TableCell align="right">{fmtMoney(je.total_credit)}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Open journal entry">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/finance-portal/journal-entries?id=${je.id}`)}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                );
            })}

            {!loading && grand.count === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontFamily }}>
                        No journal entries posted in this range. Send an invoice, record a payment,
                        approve a bill, or run payroll to see posts here.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}
